// src/components/input/Select.js
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { normalizeText } from '../../utils/stringUtils';
import './Input.css';
import './Select.css';

const SelectAddButton = ({ btnAdd, label, disabled }) => btnAdd ? (
  <button className="btn btn-input" type="button" onClick={btnAdd} disabled={disabled}
    aria-label={`Adicionar ${label?.toLocaleLowerCase('pt-BR') || 'registro'}`}
    style={{ padding: '6px', marginLeft: '8px' }} tabIndex={-1}>
    <FaPlus />
  </button>
) : null;

const SearchableSelect = ({ label, value, onChange, disabled, msgErro, style, width, autoFocus, options, placeholder, emptyMessage, btnAdd }) => {
  const inputId = useId();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuLayout, setMenuLayout] = useState({ above: false, maxHeight: 220 });
  const selectedOption = options.find((option) => String(option.value) === String(value ?? ''));
  const selectedLabel = selectedOption?.value === '' ? '' : selectedOption?.label || '';
  const filteredOptions = useMemo(() => {
    const search = normalizeText(query ?? '');
    return options.filter((option) => normalizeText(option.label).includes(search));
  }, [options, query]);

  const closeMenu = () => {
    setIsOpen(false);
    setQuery(null);
    setActiveIndex(-1);
  };

  useEffect(() => {
    setIsOpen(false);
    setQuery(null);
    setActiveIndex(-1);
  }, [value, selectedLabel, disabled]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      let top = 0;
      let bottom = window.innerHeight;

      // Respeita a área visível dos ancestrais com rolagem, incluindo o corpo do modal.
      for (let parent = wrapper.parentElement; parent; parent = parent.parentElement) {
        if (/(auto|scroll|hidden|clip)/.test(window.getComputedStyle(parent).overflowY)) {
          const parentRect = parent.getBoundingClientRect();
          top = Math.max(top, parentRect.top + parent.clientTop);
          bottom = Math.min(bottom, parentRect.top + parent.clientTop + parent.clientHeight);
        }
      }

      const below = Math.max(0, bottom - rect.bottom - 4);
      const above = Math.max(0, rect.top - top - 4);
      const desiredHeight = Math.min(220, Math.max(filteredOptions.length, 1) * 40);
      const openAbove = below < desiredHeight && above > below;
      const maxHeight = Math.min(220, openAbove ? above : below);
      setMenuLayout((previous) => previous.above === openAbove && previous.maxHeight === maxHeight
        ? previous
        : { above: openAbove, maxHeight });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, filteredOptions.length]);

  useLayoutEffect(() => {
    const list = listRef.current;
    const option = list?.children[activeIndex];
    if (!isOpen || !option) return;

    // Rola apenas a lista, sem deslocar o formulário ao navegar pelo teclado.
    if (option.offsetTop < list.scrollTop) {
      list.scrollTop = option.offsetTop;
    } else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
    }
  }, [isOpen, activeIndex]);

  const openMenu = () => {
    if (disabled || isOpen) return;
    setQuery(null);
    setIsOpen(true);
    const selectedIndex = options.findIndex((option) => !option.disabled && String(option.value) === String(value ?? ''));
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : options.findIndex((option) => !option.disabled));
    inputRef.current?.select();
  };

  const selectOption = (option) => {
    if (disabled || option.disabled) return;
    inputRef.current?.focus();
    closeMenu();
    onChange?.({ target: { value: String(option.value ?? '') } });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
    } else if (event.key === 'Tab') {
      closeMenu();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (isOpen && filteredOptions[activeIndex]) selectOption(filteredOptions[activeIndex]);
      else if (!isOpen) openMenu();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        openMenu();
        return;
      }
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      let nextIndex = activeIndex < 0 ? (direction > 0 ? -1 : 0) : activeIndex;
      for (let count = 0; count < filteredOptions.length; count += 1) {
        nextIndex = (nextIndex + direction + filteredOptions.length) % filteredOptions.length;
        if (!filteredOptions[nextIndex].disabled) {
          setActiveIndex(nextIndex);
          break;
        }
      }
    }
  };

  return (
    <div className="form-group">
      <label className="infomaio-field-label" htmlFor={inputId}>{label || '\u00A0'}</label>
      <div
        className="input-wrapper"
        style={{ width, maxWidth: '100%' }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) closeMenu();
        }}
      >
        <div className="select-searchable" ref={wrapperRef}>
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            role="combobox"
            autoComplete="off"
            value={query ?? selectedLabel}
            placeholder={placeholder}
            onFocus={openMenu}
            onClick={openMenu}
            onChange={(event) => {
              const text = event.target.value;
              setQuery(text);
              setIsOpen(true);
              setActiveIndex(options.filter((option) => normalizeText(option.label).includes(normalizeText(text)))
                .findIndex((option) => !option.disabled));
            }}
            onKeyDown={handleKeyDown}
            className={`form-control form-select ${msgErro ? 'is-invalid' : ''}`}
            disabled={disabled}
            style={{ ...style, width: '100%' }}
            autoFocus={autoFocus}
            data-modal-autofocus={autoFocus || undefined}
            aria-invalid={!!msgErro}
            aria-describedby={msgErro ? `${inputId}-error` : undefined}
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={isOpen ? `${inputId}-list` : undefined}
            aria-activedescendant={isOpen && filteredOptions[activeIndex] ? `${inputId}-option-${activeIndex}` : undefined}
          />
          {isOpen && (
            <div
              id={`${inputId}-list`}
              ref={listRef}
              role="listbox"
              aria-label={label}
              className={`select-searchable-list${menuLayout.above ? ' select-searchable-list-above' : ''}`}
              style={{ maxHeight: menuLayout.maxHeight }}
            >
              {filteredOptions.length > 0 ? filteredOptions.map((option, index) => (
                <button
                  key={`${option.value}-${index}`}
                  id={`${inputId}-option-${index}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={String(option.value) === String(value ?? '')}
                  disabled={!!option.disabled}
                  className={`select-searchable-option${index === activeIndex ? ' select-searchable-option-active' : ''}`}
                  onMouseEnter={() => { if (!option.disabled) setActiveIndex(index); }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  {option.label}
                </button>
              )) : <div className="select-searchable-empty" role="status">{emptyMessage}</div>}
            </div>
          )}
        </div>
        <SelectAddButton {...{ btnAdd, label, disabled }} />
      </div>
      {msgErro && <span className="error-select" id={`${inputId}-error`}>{msgErro}</span>}
    </div>
  );
};

const Select = ({
  label,
  value,
  onChange,
  disabled,
  msgErro,
  style,
  size,
  autoFocus,
  options,
  hidden, // 👈 nova prop
  searchable = false,
  placeholder = 'Digite para pesquisar',
  emptyMessage = 'Nenhuma opção encontrada',
  btnAdd,
}) => {
  const inputId = useId();
  // Determine the width based on the size prop
  const getWidth = () => {
    if (size === undefined) return '100px'; // Default value
    return typeof size === 'number' ? `${size}px` : size; // Append 'px' for numbers, use string as-is
  };

  // 👇 Se estiver invisível, não renderiza nada
  if (hidden) {
    return null;
  }

  if (searchable) {
    return <SearchableSelect {...{ label, value, onChange, disabled, msgErro, style, autoFocus, options, placeholder, emptyMessage, btnAdd }} width={getWidth()} />;
  }

  return (
    <div className="form-group">
      <label className="infomaio-field-label" htmlFor={inputId}>{label || '\u00A0'}</label>
      <div className="input-wrapper" style={btnAdd ? { width: getWidth(), maxWidth: '100%' } : undefined}>
        <select
          id={inputId}
          value={value}
          onChange={onChange}
          className={`form-control form-select ${msgErro ? 'is-invalid' : ''}`}
          disabled={disabled}
          style={{ ...style, width: btnAdd ? '100%' : getWidth() }}
          autoFocus={autoFocus}
          data-modal-autofocus={autoFocus || undefined}
          aria-invalid={!!msgErro}
          aria-describedby={msgErro ? `${inputId}-error` : undefined}
        >
          {options.map((option, index) => (
            <option key={index} value={option.value} disabled={option.disabled ? true : false}>
              {option.label}
            </option>
          ))}
        </select>
        <SelectAddButton {...{ btnAdd, label, disabled }} />
      </div>
      {msgErro && <span className="error-select" id={`${inputId}-error`}>{msgErro}</span>}
    </div>
  );
};

export default Select;
